import { useEffect, useState, useCallback } from "react";
import { Link } from "react-router-dom";
import api from "@/lib/api";
import logger from "@/lib/logger";
import { toast } from "sonner";
import { Card, CardContent } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Skeleton } from "@/components/ui/skeleton";
import { Checkbox } from "@/components/ui/checkbox";
import { StatusBadge } from "@/components/StatusPipeline";
import { INDIA_STATES } from "@/lib/constants";
import { MONTHS } from "@/pages/NewJob";
import { ALL_SERVICE_STATUSES } from "@/lib/serviceStatus";
import * as XLSX from "xlsx";
import {
  FileText, Download, Printer, Search, Filter, X, ChevronLeft, ChevronRight,
  ShieldCheck, BarChart2, RefreshCw, Loader2, Eye, Calendar
} from "lucide-react";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const fmtDate = d => d ? new Date(d).toLocaleDateString("en-IN", { day:"2-digit", month:"short", year:"numeric" }) : "—";
const fmtDateTime = d => d ? new Date(d).toLocaleString("en-IN", { day:"2-digit", month:"short", year:"numeric", hour:"2-digit", minute:"2-digit" }) : "—";

export default function AdminPanel() {
  const now = new Date();

  // Report filters
  const [filters, setFilters] = useState({
    from_date:    "",
    to_date:      "",
    status:       "all",
    technician:   "all",
    warranty:     "all",
    city:         "",
    search:       "",
  });
  const [searchInput, setSearchInput] = useState("");

  // Data
  const [jobs,       setJobs]       = useState([]);
  const [total,      setTotal]      = useState(0);
  const [pages,      setPages]      = useState(1);
  const [page,       setPage]       = useState(1);
  const [loading,    setLoading]    = useState(false);
  const [selected,   setSelected]   = useState(new Set());
  const [technicians,setTechnicians]= useState([]);
  const [exporting,  setExporting]  = useState(false);
  const [pdfLoading, setPdfLoading] = useState(false);

  useEffect(() => {
    api.get(`${API}/technicians`).then(r => setTechnicians(r.data.technicians || [])).catch(() => {});
  }, []);

  const fetchJobs = useCallback(async () => {
    try {
      setLoading(true);
      const params = { page, limit: 50 };
      if (filters.status !== "all") params.status = filters.status;
      if (filters.technician !== "all") params.technician = filters.technician;
      if (filters.city.trim()) params.city = filters.city.trim();
      if (filters.search.trim()) params.search = filters.search.trim();
      if (filters.from_date) params.from_date = filters.from_date;
      if (filters.to_date) params.to_date = filters.to_date;
      const res = await api.get(`${API}/jobs`, { params });
      // Client-side warranty filter
      let jobList = res.data.jobs || [];
      if (filters.warranty !== "all") {
        jobList = jobList.filter(j => (j.charger?.warranty_status || "") === filters.warranty);
      }
      setJobs(jobList);
      setTotal(res.data.total || 0);
      setPages(res.data.pages || 1);
      setSelected(new Set());
    } catch (err) {
      logger.error("[Admin] fetchJobs:", err?.message);
    } finally { setLoading(false); }
  }, [page, filters]);

  useEffect(() => { fetchJobs(); }, [fetchJobs]);

  const applySearch = () => { setFilters(f => ({ ...f, search: searchInput })); setPage(1); };
  const clearFilters = () => {
    setFilters({ from_date:"", to_date:"", status:"all", technician:"all", warranty:"all", city:"", search:"" });
    setSearchInput(""); setPage(1);
  };
  const hasFilters = filters.search || filters.status !== "all" || filters.technician !== "all" ||
    filters.warranty !== "all" || filters.city || filters.from_date || filters.to_date;

  const toggleSelect = (id) => setSelected(p => { const n=new Set(p); n.has(id)?n.delete(id):n.add(id); return n; });
  const toggleAll   = () => { if (selected.size === jobs.length) setSelected(new Set()); else setSelected(new Set(jobs.map(j=>j.id))); };

  // ── Excel Export ─────────────────────────────────────────────────────────────
  const exportExcel = async () => {
    setExporting(true);
    try {
      // Fetch ALL matching jobs (no pagination)
      const params = { page: 1, limit: 10000 };
      if (filters.status !== "all") params.status = filters.status;
      if (filters.technician !== "all") params.technician = filters.technician;
      if (filters.city.trim()) params.city = filters.city.trim();
      if (filters.search.trim()) params.search = filters.search.trim();
      if (filters.from_date) params.from_date = filters.from_date;
      if (filters.to_date) params.to_date = filters.to_date;
      const res = await api.get(`${API}/jobs`, { params });
      let all = res.data.jobs || [];
      if (filters.warranty !== "all") all = all.filter(j => (j.charger?.warranty_status||"") === filters.warranty);

      const rows = all.map((j, i) => ({
        "Sr No":              i + 1,
        "Service ID":         j.job_no || "",
        "Received Date":      fmtDate(j.extras?.received_date || j.created_at),
        "Customer Name":      j.customer?.name || "",
        "Company":            j.customer?.company_name || "",
        "Phone":              j.customer?.phone || "",
        "WhatsApp":           j.customer?.whatsapp_number || "",
        "Email":              j.customer?.email || "",
        "City":               j.customer?.city || "",
        "State":              j.customer?.state || "",
        "Charger Model":      j.charger?.charger_model || "",
        "Voltage":            j.charger?.voltage || "",
        "Ampere":             j.charger?.ampere || "",
        "Connector Type":     j.charger?.connector_type || "",
        "Serial Number":      j.charger?.serial_number || (j.products||[])[0]?.serial_number || "",
        "Mfg Month":          j.charger?.manufacturing_month ? MONTHS[j.charger.manufacturing_month-1] : "",
        "Mfg Year":           j.charger?.manufacturing_year || "",
        "Warranty Status":    j.charger?.warranty_status || "",
        "Total Chargers":     j.charger?.total_chargers || 1,
        "Problem":            j.specs?.problem_description || j.specs?.fault_description || "",
        "Service Type":       j.extras?.service_type || "",
        "Courier Received":   j.extras?.courier_received_from || "",
        "Incoming AWB":       j.extras?.courier_awb_received || "",
        "Current Status":     j.current_status || "",
        "Technician":         j.technician || "",
        "Dispatch Date":      fmtDate(j.dispatch?.dispatch_date),
        "Dispatch Courier":   j.dispatch?.courier_partner || j.courier_details?.courier_name || "",
        "Dispatch AWB":       j.dispatch?.awb_number || j.courier_details?.awb_number || "",
        "Last Updated":       fmtDateTime(j.updated_at),
      }));

      const ws = XLSX.utils.json_to_sheet(rows);
      const colWidths = Object.keys(rows[0] || {}).map(k => ({ wch: Math.max(k.length, 12) }));
      ws["!cols"] = colWidths;
      const wb = XLSX.utils.book_new();
      XLSX.utils.book_append_sheet(wb, ws, "Service Report");
      XLSX.writeFile(wb, `XSTRONG_Service_Report_${new Date().toISOString().slice(0,10)}.xlsx`);
      toast.success(`✅ ${all.length} records exported!`);
    } catch (err) {
      logger.error("[Admin] exportExcel:", err?.message);
      toast.error("Export failed");
    } finally { setExporting(false); }
  };

  // ── PDF Report ───────────────────────────────────────────────────────────────
  const exportPDF = async () => {
    setPdfLoading(true);
    try {
      const { jsPDF } = await import("jspdf");
      const autoTable  = (await import("jspdf-autotable")).default;

      const doc = new jsPDF({ orientation: "landscape", unit: "mm", format: "a4" });

      // Header
      doc.setFontSize(16);
      doc.setFont("helvetica", "bold");
      doc.setTextColor(22, 163, 74);
      doc.text("XSTRONG POWER EV CHARGER", 14, 15);
      doc.setFontSize(11);
      doc.setTextColor(100, 116, 139);
      doc.text("Service Management Report", 14, 22);
      doc.setFontSize(9);
      doc.text(`Generated: ${new Date().toLocaleString("en-IN")}  |  Total: ${total} records`, 14, 28);

      // Status summary boxes
      const statusCounts = {};
      jobs.forEach(j => { statusCounts[j.current_status] = (statusCounts[j.current_status]||0)+1; });

      autoTable(doc, {
        startY: 34,
        head: [["Service ID","Date","Customer","Phone","Serial #","Model","Status","Technician","Dispatch AWB","Last Updated"]],
        body: jobs.map(j => [
          j.job_no || "",
          fmtDate(j.extras?.received_date || j.created_at),
          `${j.customer?.name || ""}${j.customer?.company_name ? ` (${j.customer.company_name})` : ""}`,
          j.customer?.phone || "",
          j.charger?.serial_number || (j.products||[])[0]?.serial_number || "",
          `${j.charger?.charger_model||""} ${j.charger?.voltage||""} ${j.charger?.ampere||""}`.trim() || "—",
          j.current_status || "",
          j.technician || "—",
          j.dispatch?.awb_number || j.courier_details?.awb_number || "—",
          fmtDate(j.updated_at),
        ]),
        styles: { fontSize: 7, cellPadding: 2 },
        headStyles: { fillColor: [22, 163, 74], textColor: 255, fontStyle: "bold", fontSize: 7.5 },
        alternateRowStyles: { fillColor: [240, 253, 244] },
        columnStyles: { 0: { cellWidth: 22 }, 2: { cellWidth: 35 }, 5: { cellWidth: 30 } },
        margin: { left: 14, right: 14 },
      });

      doc.save(`XSTRONG_Report_${new Date().toISOString().slice(0,10)}.pdf`);
      toast.success("PDF report downloaded!");
    } catch (err) {
      logger.error("[Admin] exportPDF:", err?.message);
      toast.error("PDF export failed");
    } finally { setPdfLoading(false); }
  };

  // ── Print Job Card ───────────────────────────────────────────────────────────
  const printJobCards = () => {
    if (selected.size === 0) { toast.error("Select at least one job to print"); return; }
    const ids = jobs.filter(j => selected.has(j.id)).map(j => j.id).join(",");
    window.open(`/bulk-print?jobs=${ids}`, "_blank");
  };

  // ── Print Service Report ─────────────────────────────────────────────────────
  const printReport = () => { window.print(); };

  // ── Report Stats ─────────────────────────────────────────────────────────────
  const statusCounts = {};
  jobs.forEach(j => { statusCounts[j.current_status] = (statusCounts[j.current_status]||0)+1; });

  return (
    <div className="px-4 sm:px-6 lg:px-8 py-6">
      {/* Header */}
      <div className="flex items-center justify-between mb-6 flex-wrap gap-3">
        <div className="flex items-center gap-3">
          <div className="w-10 h-10 rounded-xl bg-green-600 flex items-center justify-center">
            <ShieldCheck size={20} className="text-white"/>
          </div>
          <div>
            <h1 className="text-2xl font-semibold text-slate-900" style={{ fontFamily:"'Space Grotesk',sans-serif" }}>Admin Panel</h1>
            <p className="text-sm text-slate-500 mt-0.5">Reports, Export, Print — Full Service Management</p>
          </div>
        </div>
        <div className="flex gap-2 flex-wrap">
          {selected.size > 0 && (
            <Button variant="outline" className="border-blue-200 text-blue-700 hover:bg-blue-50"
              onClick={printJobCards}>
              <Printer size={16} className="mr-2"/> Print {selected.size} Job Card{selected.size!==1?"s":""}
            </Button>
          )}
          <Button variant="outline" className="border-slate-200 text-slate-700 hover:bg-slate-50"
            onClick={printReport}>
            <Printer size={16} className="mr-2"/> Print Report
          </Button>
          <Button variant="outline" className="border-rose-200 text-rose-700 hover:bg-rose-50"
            onClick={exportPDF} disabled={pdfLoading}>
            {pdfLoading ? <Loader2 size={16} className="mr-2 animate-spin"/> : <FileText size={16} className="mr-2"/>}
            Export PDF
          </Button>
          <Button className="bg-green-600 hover:bg-green-700 text-white"
            onClick={exportExcel} disabled={exporting}>
            {exporting ? <Loader2 size={16} className="mr-2 animate-spin"/> : <Download size={16} className="mr-2"/>}
            Export Excel
          </Button>
        </div>
      </div>

      {/* Filters */}
      <Card className="rounded-2xl border border-slate-200/70 bg-white mb-5">
        <CardContent className="p-4">
          <div className="flex items-center gap-2 mb-3">
            <Filter size={14} className="text-slate-500"/>
            <p className="text-xs font-semibold text-slate-600 uppercase tracking-wide">Filter Report</p>
            {hasFilters && (
              <Button variant="ghost" size="sm" className="h-6 text-xs text-rose-600 hover:bg-rose-50 ml-auto" onClick={clearFilters}>
                <X size={12} className="mr-1"/>Clear All
              </Button>
            )}
          </div>
          <div className="grid grid-cols-2 sm:grid-cols-4 lg:grid-cols-7 gap-3">
            {/* Search */}
            <div className="col-span-2 sm:col-span-2 relative">
              <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-3.5 h-3.5 text-slate-400"/>
              <Input placeholder="Name, phone, serial, service ID..."
                value={searchInput} onChange={e => setSearchInput(e.target.value)}
                onKeyDown={e => { if(e.key==="Enter") applySearch(); }}
                className="pl-8 h-9 text-sm" data-testid="admin-search"/>
            </div>
            {/* From Date */}
            <div>
              <Input type="date" value={filters.from_date}
                onChange={e => { setFilters(f=>({...f,from_date:e.target.value})); setPage(1); }}
                className="h-9 text-sm" placeholder="From"/>
            </div>
            {/* To Date */}
            <div>
              <Input type="date" value={filters.to_date}
                onChange={e => { setFilters(f=>({...f,to_date:e.target.value})); setPage(1); }}
                className="h-9 text-sm" placeholder="To"/>
            </div>
            {/* Status */}
            <div>
              <Select value={filters.status} onValueChange={v => { setFilters(f=>({...f,status:v})); setPage(1); }}>
                <SelectTrigger className="h-9 text-sm" data-testid="admin-status-filter">
                  <SelectValue placeholder="All Status"/>
                </SelectTrigger>
                <SelectContent className="max-h-64">
                  <SelectItem value="all">All Status</SelectItem>
                  {ALL_SERVICE_STATUSES.map(s => <SelectItem key={s} value={s}>{s}</SelectItem>)}
                </SelectContent>
              </Select>
            </div>
            {/* Technician */}
            <div>
              <Select value={filters.technician} onValueChange={v => { setFilters(f=>({...f,technician:v})); setPage(1); }}>
                <SelectTrigger className="h-9 text-sm">
                  <SelectValue placeholder="All Tech."/>
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="all">All Technicians</SelectItem>
                  {technicians.map(t => <SelectItem key={t} value={t}>👷 {t}</SelectItem>)}
                </SelectContent>
              </Select>
            </div>
            {/* Warranty */}
            <div>
              <Select value={filters.warranty} onValueChange={v => { setFilters(f=>({...f,warranty:v})); setPage(1); }}>
                <SelectTrigger className="h-9 text-sm">
                  <SelectValue placeholder="Warranty"/>
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="all">All Warranty</SelectItem>
                  <SelectItem value="Under Warranty">Under Warranty</SelectItem>
                  <SelectItem value="Out of Warranty">Out of Warranty</SelectItem>
                  <SelectItem value="Warranty Expired">Expired</SelectItem>
                  <SelectItem value="Warranty Voided">Voided</SelectItem>
                </SelectContent>
              </Select>
            </div>
          </div>
        </CardContent>
      </Card>

      {/* Summary Stats */}
      <div className="grid grid-cols-2 sm:grid-cols-4 gap-3 mb-5">
        {[
          { label:"Total Shown", value:total, color:"text-slate-900", bg:"bg-slate-100" },
          { label:"Selected", value:selected.size, color:"text-green-700", bg:"bg-green-50" },
          { label:"Completed", value:statusCounts["Completed"]||0, color:"text-emerald-700", bg:"bg-emerald-50" },
          { label:"In Progress", value:(statusCounts["Repair in Progress"]||0)+(statusCounts["Under Inspection"]||0), color:"text-amber-700", bg:"bg-amber-50" },
        ].map(s => (
          <Card key={s.label} className="rounded-xl border border-slate-200/70">
            <CardContent className="p-3 text-center">
              <p className={`text-2xl font-bold ${s.color}`} style={{fontFamily:"'Space Grotesk',sans-serif"}}>{s.value}</p>
              <p className="text-xs text-slate-400 mt-0.5">{s.label}</p>
            </CardContent>
          </Card>
        ))}
      </div>

      {/* Jobs Table */}
      <Card className="rounded-2xl border border-slate-200/70 bg-white overflow-hidden" id="admin-report-table">
        <div className="overflow-x-auto">
          <table className="w-full text-sm">
            <thead>
              <tr className="border-b border-slate-100 bg-slate-50/80">
                <th className="px-3 py-3 w-9">
                  <Checkbox checked={jobs.length>0&&selected.size===jobs.length} onCheckedChange={toggleAll}/>
                </th>
                {["Service ID","Date","Customer","Phone","Charger / Serial","Status","Warranty","Technician","Dispatch AWB","Updated"].map(h=>(
                  <th key={h} className="text-left text-xs font-semibold text-slate-400 uppercase tracking-wide px-3 py-3 whitespace-nowrap">{h}</th>
                ))}
                <th className="px-3 py-3 w-8"></th>
              </tr>
            </thead>
            <tbody>
              {loading ? ([...Array(6)].map((_,i)=>(
                <tr key={i}>{[...Array(12)].map((_,j)=><td key={j} className="px-3 py-3"><Skeleton className="h-4"/></td>)}</tr>
              ))) : jobs.length===0 ? (
                <tr><td colSpan={12} className="px-4 py-12 text-center">
                  <div className="flex flex-col items-center gap-2">
                    <BarChart2 className="w-10 h-10 text-slate-300"/>
                    <p className="text-sm text-slate-500">No records match the current filters.</p>
                    {hasFilters && <Button variant="outline" size="sm" onClick={clearFilters}>Clear filters</Button>}
                  </div>
                </td></tr>
              ) : jobs.map(job => (
                <tr key={job.id} className={`border-b border-slate-50 hover:bg-slate-50/60 transition-colors ${selected.has(job.id)?"bg-green-50/30":""}`}>
                  <td className="px-3 py-2.5" onClick={e=>e.stopPropagation()}>
                    <Checkbox checked={selected.has(job.id)} onCheckedChange={()=>toggleSelect(job.id)}/>
                  </td>
                  <td className="px-3 py-2.5">
                    <span className="font-mono text-sm font-bold text-green-700">{job.job_no}</span>
                  </td>
                  <td className="px-3 py-2.5 whitespace-nowrap">
                    <p className="text-xs text-slate-600">{fmtDate(job.extras?.received_date||job.created_at)}</p>
                  </td>
                  <td className="px-3 py-2.5">
                    <p className="text-sm font-medium text-slate-900">{job.customer?.name}</p>
                    {job.customer?.company_name&&<p className="text-xs text-slate-400">{job.customer.company_name}</p>}
                  </td>
                  <td className="px-3 py-2.5">
                    <p className="text-sm text-slate-700">{job.customer?.phone}</p>
                    {job.customer?.whatsapp_number&&<p className="text-xs text-green-600">💬 {job.customer.whatsapp_number}</p>}
                  </td>
                  <td className="px-3 py-2.5">
                    {job.charger?.charger_model && <p className="text-xs font-medium text-slate-800">{job.charger.charger_model}</p>}
                    {(job.charger?.voltage||job.charger?.ampere) && (
                      <p className="text-xs font-mono text-green-700">{job.charger?.voltage} {job.charger?.ampere}</p>
                    )}
                    <p className="text-xs font-mono text-slate-500">{job.charger?.serial_number||(job.products||[])[0]?.serial_number||"—"}</p>
                  </td>
                  <td className="px-3 py-2.5"><StatusBadge status={job.current_status}/></td>
                  <td className="px-3 py-2.5">
                    {job.charger?.warranty_status ? (
                      <span className={`text-xs font-medium px-2 py-0.5 rounded-full border ${
                        job.charger.warranty_status==="Under Warranty"?"bg-green-50 text-green-700 border-green-200":
                        job.charger.warranty_status==="Out of Warranty"?"bg-slate-100 text-slate-600 border-slate-200":
                        "bg-rose-50 text-rose-700 border-rose-200"
                      }`}>{job.charger.warranty_status}</span>
                    ) : <span className="text-xs text-slate-300">—</span>}
                  </td>
                  <td className="px-3 py-2.5">
                    {job.technician ? <span className="text-xs text-purple-700">👷 {job.technician}</span> : <span className="text-xs text-slate-300">—</span>}
                  </td>
                  <td className="px-3 py-2.5">
                    <span className="text-xs font-mono text-slate-600">{job.dispatch?.awb_number||job.courier_details?.awb_number||"—"}</span>
                  </td>
                  <td className="px-3 py-2.5 whitespace-nowrap">
                    <p className="text-xs text-slate-400">{fmtDate(job.updated_at)}</p>
                  </td>
                  <td className="px-3 py-2.5">
                    <Link to={`/jobs/${job.id}`}>
                      <Button variant="ghost" size="icon" className="h-7 w-7 text-slate-400 hover:text-green-600"><Eye size={13}/></Button>
                    </Link>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>

        {pages>1&&(
          <div className="flex items-center justify-between px-4 py-3 border-t border-slate-100">
            <p className="text-xs text-slate-500">Showing {(page-1)*50+1}–{Math.min(page*50,total)} of {total}</p>
            <div className="flex items-center gap-1">
              <Button variant="outline" size="icon" className="h-7 w-7" disabled={page===1} onClick={()=>setPage(p=>p-1)}><ChevronLeft size={13}/></Button>
              <span className="text-xs text-slate-600 px-2">Page {page} of {pages}</span>
              <Button variant="outline" size="icon" className="h-7 w-7" disabled={page===pages} onClick={()=>setPage(p=>p+1)}><ChevronRight size={13}/></Button>
            </div>
          </div>
        )}
      </Card>

      {/* Print styles */}
      <style>{`
        @media print {
          .no-print, button, [role="button"] { display: none !important; }
          body { font-size: 10px; }
        }
      `}</style>
    </div>
  );
}
